import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { esCancelacion, mensajeErrorAuth } from '../../utils/authErrores';
import { useCerrarConEscape } from '../../hooks/useCerrarConEscape';

const PALABRA = 'ELIMINAR';

/**
 * Borrar la cuenta para siempre. Apple obliga a que se pueda hacer desde la
 * propia app (directriz 5.1.1(v)), y el RGPD lo pide igualmente en la web.
 * Antes de nada hay que volver a identificarse: el servidor no acepta una
 * sesión abierta hace más de unos minutos.
 */
export const ModalEliminarCuenta: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { borrarCuenta, metodoReautenticacion } = useAuth();
  const metodo = metodoReautenticacion();
  const [confirmacion, setConfirmacion] = useState('');
  const [password, setPassword] = useState('');
  const [borrando, setBorrando] = useState(false);
  // Mientras se borra no se puede cerrar (tampoco con Esc)
  useCerrarConEscape(() => { if (!borrando) onClose(); });
  const [error, setError] = useState('');

  const listo = confirmacion.trim().toUpperCase() === PALABRA && (metodo !== 'password' || password.length > 0);

  const borrar = async () => {
    setError(''); setBorrando(true);
    try {
      // Al cerrarse la sesión, PrivateRoute lleva solo a la pantalla de entrada
      await borrarCuenta(metodo === 'password' ? password : undefined);
    } catch (e) {
      if (!esCancelacion(e)) setError(mensajeErrorAuth(e, 'No se ha podido borrar la cuenta.'));
      setBorrando(false);
    }
  };

  const textoBoton = borrando ? 'Borrando…'
    : metodo === 'apple'  ? 'Confirmar con Apple y borrar'
    : metodo === 'google' ? 'Confirmar con Google y borrar'
    : 'Borrar mi cuenta';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center capa-modal z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-full overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 dark:bg-red-900/40 p-2 rounded-full">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Eliminar mi cuenta</h2>
          </div>
          <button type="button" onClick={onClose} disabled={borrando} aria-label="Cerrar"
            className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>Se borrará <strong>para siempre</strong>, sin forma de recuperarlo:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Tu cuenta y tus métodos de acceso.</li>
            <li>Todo lo tuyo en Finanzas, Taller 3D, Clientes y Préstamos.</li>
            <li>Los planes de A medias en los que solo estás tú.</li>
          </ul>
          <p>
            En los planes compartidos con más gente se conservan los gastos y pagos, porque
            son las cuentas del grupo; tú dejas de ser miembro y tu nombre queda como un
            participante sin cuenta. Si eras el único organizador, pasa a serlo otro miembro.
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Si quieres guardar una copia, expórtala antes desde Configuración → Datos.
          </p>
        </div>

        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-1.5">
          Escribe <span className="font-mono">{PALABRA}</span> para confirmar
        </label>
        <input value={confirmacion} onChange={e => setConfirmacion(e.target.value)} disabled={borrando}
          autoComplete="off"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-red-500
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />

        {metodo === 'password' && (
          <>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mt-4 mb-1.5">
              Tu contraseña
            </label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} disabled={borrando}
              autoComplete="current-password"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-red-500
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </>
        )}
        {metodo !== 'password' && metodo !== null && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            Se abrirá {metodo === 'apple' ? 'Apple' : 'Google'} para confirmar que eres tú.
          </p>
        )}

        {error && <p className="text-sm text-red-600 dark:text-red-400 mt-3">{error}</p>}

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} disabled={borrando}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-semibold
                       text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            Cancelar
          </button>
          <button onClick={borrar} disabled={!listo || borrando}
            className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold
                       disabled:opacity-40 disabled:cursor-not-allowed transition">
            {textoBoton}
          </button>
        </div>
      </div>
    </div>
  );
};
